import { PHONE_PATTERN, normalizePhone } from "@car/auth/roles";
import { z } from "zod";

// «تابان شیپ»: حمل دریایی خودروی مشتری. این ماژول خالص است (فقط zod و @car/auth/roles) تا سایت
// حمل، پنل مدیریت و API همین کلیدها، برچسب‌ها و قواعد را بخوانند. جدول‌ها: @car/db/schema/shipping.

// ---------------------------------------------------------------------------
// Keys and labels
// ---------------------------------------------------------------------------

export const shipmentStageKeys = [
  "requested",
  "origin_received",
  "warehouse",
  "loaded",
  "sailing",
  "arrived",
  "clearance",
  "delivered",
] as const;
export type ShipmentStage = (typeof shipmentStageKeys)[number];

export const shipmentStageLabels = {
  requested: "ثبت درخواست",
  origin_received: "دریافت خودرو در مبدأ",
  warehouse: "ورود به انبار",
  loaded: "بارگیری روی شناور",
  sailing: "حرکت دریایی",
  arrived: "رسیدن به بندر مقصد",
  clearance: "آماده‌سازی مدارک و تسویه",
  delivered: "تحویل نهایی",
} satisfies Record<ShipmentStage, string>;

export const stageIndex = (stage: ShipmentStage) => shipmentStageKeys.indexOf(stage);

export const shipmentStateKeys = [
  "pending",
  "active",
  "delivered",
  "rejected",
  "cancelled",
] as const;
export type ShipmentState = (typeof shipmentStateKeys)[number];

export const shipmentStateLabels = {
  pending: "در انتظار بررسی",
  active: "در جریان",
  delivered: "تحویل‌شده",
  rejected: "رد شد",
  cancelled: "لغو شد",
} satisfies Record<ShipmentState, string>;

/** shown next to the stage while the shipment is held; the reason is `hold` */
export const HELD_LABEL = "متوقف";

export const vehicleTypeKeys = ["sedan", "suv", "pickup", "van", "other"] as const;
export type VehicleType = (typeof vehicleTypeKeys)[number];

export const vehicleTypeLabels = {
  sedan: "سواری",
  suv: "شاسی‌بلند",
  pickup: "وانت",
  van: "ون",
  other: "سایر",
} satisfies Record<VehicleType, string>;

export const shipmentEventKinds = [
  "submitted",
  "accepted",
  "rejected",
  "cancelled",
  "stage",
  "hold",
  "resume",
  "note",
  "undo",
] as const;
export type ShipmentEventKind = (typeof shipmentEventKinds)[number];

export const shipmentFileKinds = [
  "contract",
  "handover",
  "bill_of_lading",
  "photo",
  "customs",
  "invoice",
  "other",
] as const;
export type ShipmentFileKind = (typeof shipmentFileKinds)[number];

export const shipmentFileKindLabels = {
  contract: "توافق‌نامه امضاشده",
  handover: "صورت‌جلسه امضاشده",
  bill_of_lading: "بارنامه",
  photo: "عکس",
  customs: "اسناد گمرکی",
  invoice: "فاکتور و رسید",
  other: "سایر",
} satisfies Record<ShipmentFileKind, string>;

/** the PDF's «مرحله»: who hands the car over at this checkpoint */
export const handoverParties = ["origin", "carrier", "warehouse", "dealer", "customer"] as const;
export type HandoverParty = (typeof handoverParties)[number];

export const handoverPartyLabels = {
  origin: "مبدأ",
  carrier: "حمل‌کننده",
  warehouse: "انبار",
  dealer: "نمایندگی",
  customer: "مشتری",
} satisfies Record<HandoverParty, string>;

export const checklistKeys = [
  "vin_match",
  "body",
  "bumpers",
  "glass",
  "tyres",
  "interior",
  "doors",
  "startup",
  "fluids",
  "electrics",
  "keys",
  "tools",
  "documents",
] as const;
export type ChecklistKey = (typeof checklistKeys)[number];

// صورت‌جلسه‌ی تحویل و چک‌لیست وضعیت خودرو (TabanVehicleHandoverChecklist.pdf). تعداد کلید و مدارک
// فیلد جدا دارد (keysQty، docsQty)، پس جای خالیِ «تعداد: …» جزو برچسب نیست.
export const checklistLabels = {
  vin_match: "تطبیق شاسی، سال و رنگ با مدارک",
  body: "بدنه، رنگ، خط‌وخش و فرورفتگی",
  bumpers: "سپرها، جلوپنجره و تزئینات بیرونی",
  glass: "شیشه‌ها، آینه‌ها و چراغ‌ها",
  tyres: "لاستیک‌ها، رینگ‌ها، زاپاس یا کیت پنچری",
  interior: "صندلی، تودوزی، داشبورد و صندوق",
  doors: "درها، قفل‌ها، شیشه‌بالابر و سقف",
  startup: "روشن‌شدن، چراغ هشدار و باتری",
  fluids: "نشتی ظاهری و سطح مایعات",
  electrics: "عملکرد چراغ، بوق، برف‌پاک‌کن و تهویه",
  keys: "کلید و ریموت",
  tools: "ابزار، جک، لوازم جانبی و اقلام همراه",
  documents: "مدارک تحویلی",
} satisfies Record<ChecklistKey, string>;

export const checklistLabelsEn = {
  vin_match: "VIN / year / colour vs. documents",
  body: "Body panels / paint / visible damage",
  bumpers: "Bumpers / grille / exterior trim",
  glass: "Glass / mirrors / lamps",
  tyres: "Tyres / wheels / spare or repair kit",
  interior: "Seats / trim / dashboard / luggage area",
  doors: "Doors / locks / windows / roof",
  startup: "Start-up / warning lights / battery",
  fluids: "Visible fluid leaks / fluid levels",
  electrics: "Lights / horn / wipers / air conditioning",
  keys: "Keys / remotes",
  tools: "Tools / jack / accessories / loose items",
  documents: "Documents handed over",
} satisfies Record<ChecklistKey, string>;

/** S = satisfactory / complete, D = damage / missing, N = not checked / not applicable */
export const checklistMarks = ["S", "D", "N"] as const;
export type ChecklistMark = (typeof checklistMarks)[number];

export const markLabels = {
  S: "سالم/کامل",
  D: "آسیب/کسری",
  N: "بررسی‌نشده/نامرتبط",
} satisfies Record<ChecklistMark, string>;

/** a move into one of these stages sends the customer an SMS by default; «ورود به انبار» does not */
export const SMS_STAGES: readonly ShipmentStage[] = [
  "origin_received",
  "loaded",
  "sailing",
  "arrived",
  "clearance",
  "delivered",
];

/** the contracting party of the agreement and the handover report */
export const TABAN_SHIP_COMPANY = {
  name: "اقتصاد تابان فردای کیش",
  nameEn: "Eghtesad Taban Fardaye Kish",
  registrationNo: "14666",
  address: "کیش، میدان امیرکبیر، بلوار رودکی، رودکی ۴",
  addressEn: "Amir Kabir Square, Rudaki Blvd., Rudaki 4, Kish",
};

// ---------------------------------------------------------------------------
// Tracking code and masking
// ---------------------------------------------------------------------------

const toPersianDigits = (value: string) =>
  value.replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)] ?? digit);

const toLatinDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

/** uniform integer in [0, n): values from the biased tail of the 32-bit range are redrawn */
const randomBelow = (n: number) => {
  const limit = 2 ** 32 - (2 ** 32 % n);
  const draw = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(draw);
    const value = draw[0] ?? 0;
    if (value < limit) return value % n;
  }
};

/** ten digits, the first never 0: the only thing a customer needs to follow the car */
export const newTrackingCode = () =>
  String(1 + randomBelow(9)) + Array.from({ length: 9 }, () => randomBelow(10)).join("");

/**
 * What a customer typed or pasted → the ten-digit code, or null. Dropped: spaces, «-» and its
 * Unicode cousins (U+2010–U+2015), zero-width and direction marks (U+200B–U+200F), and the bidi
 * embeddings and isolates an SMS app wraps a number in (U+202A–U+202E, U+2066–U+2069).
 */
export function normalizeTrackingCode(input: string): string | null {
  const code = toLatinDigits(input).replace(
    /[\s\-\u{200B}-\u{200F}\u{202A}-\u{202E}\u{2066}-\u{2069}\u{2010}-\u{2015}]/gu,
    "",
  );
  return /^[1-9]\d{9}$/.test(code) ? code : null;
}

export const formatTrackingCode = (code: string) => {
  const digits = toPersianDigits(code);
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

/** first name + the first letter of the surname + «…»; a single word shows only its first letter */
export function maskName(name: string) {
  const [first = "", ...rest] = name.trim().split(/\s+/);
  const last = rest.at(-1);
  return last === undefined ? `${[...first][0] ?? ""}…` : `${first} ${[...last][0] ?? ""}…`;
}

/** ۰۹۱۲•••۴۵۶۷: a stored mobile is always 11 digits, anything else is hidden entirely */
export const maskPhone = (phone: string) =>
  phone.length === 11
    ? toPersianDigits(`${phone.slice(0, 4)}•••${phone.slice(-4)}`)
    : "•".repeat(phone.length);

/** first 3 + dots + last 4; a value of 7 characters or fewer is hidden entirely */
export const maskVin = (vin: string) =>
  vin.length <= 7
    ? "•".repeat(vin.length)
    : `${vin.slice(0, 3)}${"•".repeat(vin.length - 7)}${vin.slice(-4)}`;

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/** null = allowed; staff may jump over stages, never go back (that is an undo) */
export function canMove(state: ShipmentState, from: ShipmentStage, to: ShipmentStage) {
  if (state !== "active") return "فقط پرونده‌ی در جریان مرحله می‌گیرد.";
  if (stageIndex(to) <= stageIndex(from)) return "مرحله‌ی تازه باید بعد از مرحله‌ی فعلی باشد.";
  return null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const TEHRAN_OFFSET_MS = 3.5 * 60 * 60 * 1000;

/** Tehran is UTC+03:30 with no daylight saving since 1401 */
const startOfTehranDayMs = (now: Date) => {
  const shifted = now.getTime() + TEHRAN_OFFSET_MS;
  return shifted - (shifted % DAY_MS) - TEHRAN_OFFSET_MS;
};

/** the handover day at the origin: from the start of today in Tehran up to now + 366 days */
export function checkHandoverDate(iso: string, now: Date) {
  const at = Date.parse(iso);
  if (!(at >= startOfTehranDayMs(now))) return "تاریخ تحویل در مبدأ نمی‌تواند گذشته باشد.";
  if (at > now.getTime() + 366 * DAY_MS) return "تاریخ تحویل در مبدأ باید تا یک سال آینده باشد.";
  return null;
}

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

const text = (min: number, max: number) => z.string().trim().min(min).max(max);
const isoDate = z.iso.datetime();
const money = z.number().int().min(0).nullable();

const phoneSchema = z
  .string()
  .trim()
  .transform(normalizePhone)
  .refine((phone) => PHONE_PATTERN.test(phone), "شماره موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد.");

/** Gregorian, 1990 up to next year; read when parsing, not when the module loads */
const modelYearSchema = z
  .number()
  .int()
  .refine(
    (year) => year >= 1990 && year <= new Date().getUTCFullYear() + 1,
    "سال مدل باید میلادی و از ۱۹۹۰ تا سال آینده باشد.",
  );

// {نام} and {خودرو} go into a «تابان شیپ:» SMS to any phone: no dot, slash, colon or @ means no
// link or address can ride on the company's line. The shipping form shows the same messages.
/** letters of any script, their marks, spaces and ZWNJ */
export const PERSON_NAME_PATTERN = /^[\p{L}\p{M} \u{200C}]+$/u;
export const PERSON_NAME_MESSAGE = "نام فقط می‌تواند حروف و فاصله داشته باشد.";
/** the name's characters plus Latin, Persian and Arabic digits and «-» */
export const CAR_TEXT_PATTERN = /^[\p{L}\p{M}\d\u{06F0}-\u{06F9}\u{0660}-\u{0669} \u{200C}-]+$/u;
export const CAR_TEXT_MESSAGE = "فقط حروف، رقم، فاصله و خط تیره مجاز است.";
/**
 * Five digits in a row is a phone number in the SMS, so a run counts across what reads as a gap:
 * space, ZWNJ, «-», marks and tatweel. ponytail: digits split by real letters still pass.
 */
export const LONG_NUMBER_PATTERN =
  /[\d\u{06F0}-\u{06F9}\u{0660}-\u{0669}](?:[ \u{200C}\p{M}\p{Lm}-]*[\d\u{06F0}-\u{06F9}\u{0660}-\u{0669}]){4}/u;
export const LONG_NUMBER_MESSAGE = "عدد بیش از چهار رقم مجاز نیست.";
export const SPLIT_NUMBER_MESSAGE = "برند و مدل کنار هم عددی بیش از چهار رقم می‌سازند.";

/** {خودرو} puts the brand right before the model: a run that neither field has on its own */
export const splitsNumber = (brand: string, model: string) =>
  LONG_NUMBER_PATTERN.test(`${brand} ${model}`) &&
  !LONG_NUMBER_PATTERN.test(brand) &&
  !LONG_NUMBER_PATTERN.test(model);

const carText = (max: number) =>
  text(1, max)
    .regex(CAR_TEXT_PATTERN, CAR_TEXT_MESSAGE)
    .refine((value) => !LONG_NUMBER_PATTERN.test(value), LONG_NUMBER_MESSAGE);

// shipmentFieldsSchema omits from these fields: zod refuses .omit on a refined object
const requestFields = z.object({
  name: text(2, 80).regex(PERSON_NAME_PATTERN, PERSON_NAME_MESSAGE),
  phone: phoneSchema,
  brand: carText(40),
  model: carText(60),
  modelYear: modelYearSchema,
  color: carText(30),
  vehicleType: z.enum(vehicleTypeKeys),
  routeId: z.string().trim().max(60),
  originHandoverIso: isoDate,
});

/** what the public form submits */
export const shipmentRequestSchema = requestFields.refine(
  (value) => !splitsNumber(value.brand, value.model),
  { path: ["model"], message: SPLIT_NUMBER_MESSAGE },
);

/** everything staff may edit on a file: the submitted car and customer plus the admin-only fields */
export const shipmentFieldsSchema = requestFields
  .omit({ routeId: true, originHandoverIso: true })
  .extend({
    // signed-in staff, not the open form: plain text, so no older file is locked out of an edit
    name: text(2, 80),
    brand: text(1, 40),
    model: text(1, 60),
    color: text(1, 30),
    formNumber: text(0, 40),
    contractNumber: text(0, 40),
    vin: z.string().trim().toUpperCase().max(20),
    engineNumber: text(0, 30),
    plate: text(0, 30),
    destinationReceivedIso: isoDate.nullable(),
    transitDays: z.number().int().min(1).max(120).nullable(),
    vehicleValue: money,
    vessel: text(0, 80),
    voyage: text(0, 40),
    departedIso: isoDate.nullable(),
    etaIso: isoDate.nullable(),
    freight: money,
    advancePaid: money,
    lateFee: money,
    costChangeDays: z.number().int().min(1).max(60).nullable(),
  });
export type ShipmentFields = z.infer<typeof shipmentFieldsSchema>;

/** every row has a mark or null (not filled yet) */
export const checklistItemsSchema = z.record(
  z.enum(checklistKeys),
  z.enum(checklistMarks).nullable(),
);

const signatorySchema = z.object({
  name: text(0, 80),
  role: text(0, 80),
  idOrMobile: text(0, 40),
});
const emptySignatory = { name: "", role: "", idOrMobile: "" };

/** the digital handover report; a D or N row needs the note that explains it */
export const inspectionInputSchema = z
  .object({
    stage: z.enum(shipmentStageKeys),
    party: z.enum(handoverParties),
    atIso: isoDate,
    place: text(0, 120).default(""),
    odometerKm: z.number().int().min(0).max(2_000_000).nullable().default(null),
    fuelPercent: z.number().int().min(0).max(100).nullable().default(null),
    plate: text(0, 30).default(""),
    items: checklistItemsSchema,
    keysQty: z.number().int().min(0).max(99).nullable().default(null),
    docsQty: z.number().int().min(0).max(99).nullable().default(null),
    damageNote: text(0, 2000).default(""),
    deliveredItems: text(0, 2000).default(""),
    deliverer: signatorySchema.default(emptySignatory),
    receiver: signatorySchema.default(emptySignatory),
    visible: z.boolean().default(true),
  })
  .refine(
    (value) =>
      value.damageNote !== "" ||
      !Object.values(value.items).some((mark) => mark === "D" || mark === "N"),
    { path: ["damageNote"], message: "شرح آسیب یا علت بررسی‌نشدن را بنویسید." },
  );

export const routeInputSchema = z.object({
  origin: text(2, 80),
  destination: text(2, 80),
  transitDays: z.number().int().min(1).max(120).nullable(),
  active: z.boolean().default(true),
  sort: z.number().int().min(0).max(999).default(0),
});

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

export type StageStatus = "done" | "current" | "held" | "upcoming";
/** every non-revoked `submitted` / `stage` event, visible or not */
export type TimelineMove = { stage: ShipmentStage; occurredIso: string };
/** a visible, non-revoked, non-empty note */
export type TimelineNote = { stage: ShipmentStage; occurredIso: string; text: string };
export type TimelineEntry = {
  stage: ShipmentStage;
  status: StageStatus;
  dateIso: string | null;
  notes: { iso: string; text: string }[];
};

const closedStates: readonly ShipmentState[] = ["delivered", "rejected", "cancelled"];

/**
 * The eight stages as the customer sees them. A stage skipped by a jump takes the date of the
 * earliest move at or after it; an upcoming stage has no date.
 */
export function timelineOf(input: {
  state: ShipmentState;
  stage: ShipmentStage;
  hold: string | null;
  moves: TimelineMove[];
  notes: TimelineNote[];
}): TimelineEntry[] {
  const current = stageIndex(input.stage);
  return shipmentStageKeys.map((stage, index) => {
    const status: StageStatus =
      index < current || (index === current && closedStates.includes(input.state))
        ? "done"
        : index > current
          ? "upcoming"
          : input.hold !== null
            ? "held"
            : "current";
    const dateIso =
      status === "upcoming"
        ? null
        : input.moves
            .filter((move) => stageIndex(move.stage) >= index)
            .reduce<string | null>(
              (earliest, move) =>
                earliest === null || Date.parse(move.occurredIso) < Date.parse(earliest)
                  ? move.occurredIso
                  : earliest,
              null,
            );
    const notes = input.notes
      .filter((note) => note.stage === stage)
      .sort((a, b) => Date.parse(a.occurredIso) - Date.parse(b.occurredIso))
      .map((note) => ({ iso: note.occurredIso, text: note.text }));
    return { stage, status, dateIso, notes };
  });
}

// ---------------------------------------------------------------------------
// The public view of a shipment (tracking): masked, no staff data
// ---------------------------------------------------------------------------

export type PublicShipment = {
  code: string;
  state: ShipmentState;
  stage: ShipmentStage;
  hold: string | null;
  closeReason: string;
  updatedIso: string;
  vehicle: {
    brand: string;
    model: string;
    modelYear: number;
    color: string;
    vehicleType: VehicleType;
    vinMasked: string;
  };
  route: { origin: string; destination: string; transitDays: number | null };
  file: {
    contractNumber: string;
    formNumber: string;
    vessel: string;
    voyage: string;
    originHandoverIso: string;
    departedIso: string | null;
    etaIso: string | null;
    destinationReceivedIso: string | null;
  };
  /** masked */
  customer: { name: string; phone: string };
  /** freight − advancePaid; null when freight is null */
  balance: number | null;
  timeline: TimelineEntry[];
  inspections: {
    id: string;
    number: string;
    stage: ShipmentStage;
    party: HandoverParty;
    atIso: string;
    place: string;
    odometerKm: number | null;
    fuelPercent: number | null;
    tally: { S: number; D: number; N: number; unset: number };
    flagged: { key: ChecklistKey; mark: "D" | "N" }[];
    damageNote: string;
  }[];
  photos: { id: string; stage: ShipmentStage; inspectionId: string | null; url: string }[];
  documents: { title: string; kind: ShipmentFileKind; stage: ShipmentStage }[];
};
